import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProposalField {
  sectionName: string;
  fieldName: string;
  fieldValue: string;
  confidence?: 'high' | 'medium' | 'low';
  // For xlsx-encoded fields, the backend stores a human-readable label
  // (e.g. "Building [A5]") so the editor can show what the cell represents.
  displayLabel?: string;
  // Repeating-unit label within a section (e.g. "Location 1"); lets the editor
  // group a section's fields into removable rows.
  rowLabel?: string;
  // Multi-template proposals: which source template this field came from.
  templateId?: string;
  placeholder?: string;
}

interface Proposal {
  id: string;
  title: string;
  templateId: string | null;
  templateIds?: string[];
  sourceReportId: string | null;
  instructions: string | null;
  fields: ProposalField[];
  status: 'draft' | 'final';
  createdAt: string;
  template?: {
    id: string;
    title: string;
    originalFileName: string;
    originalFileType: string;
  };
}

// CSS for the rendered document view. Mirrors the export fallback styling so
// the on-screen proposal reads like the real document, and styles the inline
// editable boxes (highlighted by AI confidence).
const DOCUMENT_VIEW_CSS = `
.rn-doc { font-family: 'Segoe UI', Arial, Helvetica, sans-serif; color: #333; font-size: 10.5pt; line-height: 1.5; }
.rn-doc h1 { font-size: 18pt; color: #1a3a5c; margin: 20pt 0 8pt; }
.rn-doc h2 { font-size: 14pt; color: #1a3a5c; border-bottom: 2px solid #1a3a5c; padding-bottom: 4pt; margin: 16pt 0 8pt; }
.rn-doc h3 { font-size: 12pt; color: #444; margin: 12pt 0 6pt; }
.rn-doc p { margin: 4pt 0; }
.rn-doc table { width: 100%; border-collapse: collapse; margin: 10pt 0; font-size: 10pt; }
.rn-doc td, .rn-doc th { border: 1px solid #bbb; padding: 6pt 10pt; text-align: left; vertical-align: top; }
.rn-doc th { background: #e8eef4; font-weight: bold; color: #1a3a5c; }
.rn-doc strong { color: #1a3a5c; }
.rn-doc .rn-field {
  display: inline-block;
  min-width: 60px;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid transparent;
  background: rgba(25, 147, 229, 0.08);
  box-shadow: inset 0 -1px 0 rgba(25, 147, 229, 0.4);
  cursor: text;
  white-space: pre-wrap;
  transition: background 0.15s, border-color 0.15s;
}
.rn-doc .rn-field:hover { background: rgba(25, 147, 229, 0.16); }
.rn-doc .rn-field:focus { outline: none; border-color: #1993e5; background: #fff; box-shadow: 0 0 0 2px rgba(25, 147, 229, 0.35); }
.rn-doc .rn-field[data-confidence="low"] { background: rgba(239, 68, 68, 0.1); box-shadow: inset 0 -1px 0 rgba(239, 68, 68, 0.4); }
.rn-doc .rn-field[data-confidence="low"]:hover { background: rgba(239, 68, 68, 0.18); }
.rn-doc .rn-field[data-confidence="medium"] { background: rgba(234, 179, 8, 0.12); box-shadow: inset 0 -1px 0 rgba(234, 179, 8, 0.5); }
.rn-doc .rn-field[data-confidence="medium"]:hover { background: rgba(234, 179, 8, 0.2); }
.rn-doc .rn-field:empty::before { content: 'Enter value…'; color: #9ca3af; font-style: italic; }

/* Section (table) removal */
.rn-section-wrap { position: relative; }
.rn-section-toolbar { position: absolute; top: 4px; right: 4px; z-index: 5; }
.rn-section-btn {
  font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 6px; cursor: pointer;
  border: 1px solid #d1d5db; background: #fff; color: #6b7280; opacity: 0; transition: opacity 0.15s;
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.rn-section-wrap:hover .rn-section-btn { opacity: 1; }
.rn-section-btn.rn-remove:hover { color: #dc2626; border-color: #fecaca; background: #fef2f2; }
.rn-section-btn.rn-restore { opacity: 1; color: #16a34a; border-color: #bbf7d0; background: #f0fdf4; }
.rn-section-wrap.rn-section-removed .rn-doc-overlay,
.rn-section-wrap.rn-section-removed table { opacity: 0.4; }
.rn-section-wrap.rn-section-removed table { filter: grayscale(0.6); }
.rn-section-wrap.rn-section-removed::after {
  content: ''; position: absolute; inset: 0; background: repeating-linear-gradient(135deg, rgba(220,38,38,0.04) 0 10px, transparent 10px 20px); pointer-events: none;
}
.rn-doc .rn-field.rn-removed { background: rgba(148,163,184,0.12); box-shadow: none; text-decoration: line-through; color: #94a3b8; cursor: default; }
.rn-doc .rn-field.rn-removed:empty::before { content: ''; }
/* Per-row remove control (finer than whole-section removal) */
.rn-row-remove {
  position: absolute; top: 50%; right: 2px; transform: translateY(-50%);
  width: 18px; height: 18px; line-height: 1; font-size: 11px; border-radius: 9999px;
  border: 1px solid #fecaca; background: #fef2f2; color: #dc2626; cursor: pointer;
  opacity: 0; transition: opacity 0.12s; z-index: 6; padding: 0;
}
.rn-doc tr:hover .rn-row-remove { opacity: 1; }
.rn-doc tr.rn-row-removed .rn-row-remove { opacity: 1; color: #16a34a; border-color: #bbf7d0; background: #f0fdf4; }
.rn-doc tr.rn-row-removed td, .rn-doc tr.rn-row-removed th { opacity: 0.45; }
.rn-doc .rn-heading-removed { opacity: 0.4; text-decoration: line-through; }
.rn-doc .rn-template-divider {
  margin: 36px -48px 24px; padding: 8px 48px;
  background: #f1f5f9; border-top: 2px solid #1993e5;
  color: #334155; font-weight: 600; font-size: 13px;
  text-transform: uppercase; letter-spacing: 0.05em;
}
.rn-doc .rn-template-divider:first-child { margin-top: -40px; border-top: none; border-bottom: 2px solid #1993e5; }
`;

export interface RestoreItem {
  fieldName: string;
  section: string;
  label: string;
  // Multi-template proposals: needed so a re-created field can be mapped back
  // to its template token on export.
  templateId?: string;
  placeholder?: string;
}

// Read a RestoreItem off an editable field span's data attributes.
const restoreItemFromSpan = (el: HTMLElement, fieldName: string): RestoreItem => ({
  fieldName,
  section: el.getAttribute('data-section') || '',
  label: el.getAttribute('data-label') || '',
  templateId: el.getAttribute('data-template-id') || undefined,
  placeholder: el.getAttribute('data-placeholder') || undefined,
});

interface ProposalDocumentViewProps {
  apiUrl: string;
  proposalId: string;
  token: string | null;
  fields: ProposalField[];
  removedFieldNames: Set<string>;
  onFieldChangeByName: (fieldName: string, value: string) => void;
  onRemoveFields: (fieldNames: string[]) => void;
  onRestoreFields: (items: RestoreItem[]) => void;
  onSupportResolved: (supported: boolean) => void;
}

// One rendered <table> = one removable "section". We anchor a Remove/Restore
// control to each table and act on the field names it contains.
interface SectionMeta {
  wrap: HTMLElement;
  button: HTMLButtonElement;
  fieldNames: string[];
  items: RestoreItem[];   // for re-creating fields on restore
  label: string;
  // Heading element(s) directly above the table (the section's title): dimmed
  // along with the section when removed, mirroring export's header stripping.
  headerEls?: HTMLElement[];
}

const ProposalDocumentView: React.FC<ProposalDocumentViewProps> = ({
  apiUrl,
  proposalId,
  token,
  fields,
  removedFieldNames,
  onFieldChangeByName,
  onRemoveFields,
  onRestoreFields,
  onSupportResolved,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Latest callbacks via refs so listeners attached once stay current.
  const changeRef = React.useRef(onFieldChangeByName);
  const removeRef = React.useRef(onRemoveFields);
  const restoreRef = React.useRef(onRestoreFields);
  changeRef.current = onFieldChangeByName;
  removeRef.current = onRemoveFields;
  restoreRef.current = onRestoreFields;

  // Removed-section tracking. serverRemoved = absent from saved fields at load.
  // removedLocal = removed via the buttons this session (kept here so dimming
  // survives a Save, which clears the parent's removed set). restored = sections
  // the user brought back. The parent's removedFieldNames also feeds in so a
  // removal done in the Fields view shows here too.
  const serverRemovedRef = React.useRef<Set<string>>(new Set());
  const [removedLocal, setRemovedLocal] = useState<Set<string>>(new Set());
  const [restored, setRestored] = useState<Set<string>>(new Set());
  const sectionsRef = React.useRef<SectionMeta[]>([]);
  const rowsRef = React.useRef<SectionMeta[]>([]);
  // Latest isRemoved for use inside once-attached row/section click listeners.
  const isRemovedRef = React.useRef<(n: string) => boolean>(() => false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/proposals/${proposalId}/editable-html`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load document view');
        const data = await res.json();
        if (cancelled) return;
        if (!data.supported) {
          onSupportResolved(false);
          return;
        }
        onSupportResolved(true);
        setHtml(data.html || '');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load document view');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Fetch once per proposal; value sync is handled by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, proposalId, token]);

  // Whether a field counts as removed right now: a server-removed field is
  // removed unless the user restored it; an otherwise-present field is removed
  // if the parent's removed set contains it.
  const isRemoved = useCallback((name: string): boolean => {
    if (restored.has(name)) return false;
    return serverRemovedRef.current.has(name) || removedLocal.has(name) || removedFieldNames.has(name);
  }, [removedFieldNames, removedLocal, restored]);
  isRemovedRef.current = isRemoved;

  // After the HTML mounts: capture server-removed fields, wire editing input,
  // and build a Remove/Restore control on each table.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || html == null) return;

    // Capture which fields the server reports as already removed.
    const serverRemoved = new Set<string>();
    container.querySelectorAll<HTMLElement>('.rn-field[data-removed="1"]').forEach(el => {
      const n = el.getAttribute('data-field');
      if (n) serverRemoved.add(n);
    });
    serverRemovedRef.current = serverRemoved;

    // Delegated input -> push edits up by field name.
    const handleInput = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target && target.classList.contains('rn-field')) {
        const name = target.getAttribute('data-field');
        if (name) changeRef.current(name, target.textContent || '');
      }
    };
    container.addEventListener('input', handleInput);

    // Build per-table sections with a toolbar button.
    const sections: SectionMeta[] = [];
    container.querySelectorAll<HTMLElement>('table').forEach(table => {
      const spans = Array.from(table.querySelectorAll<HTMLElement>('.rn-field'));
      if (spans.length === 0) return;
      const fieldNames: string[] = [];
      const items: RestoreItem[] = [];
      const sectionCounts: Record<string, number> = {};
      spans.forEach(el => {
        const n = el.getAttribute('data-field');
        if (!n) return;
        fieldNames.push(n);
        const item = restoreItemFromSpan(el, n);
        items.push(item);
        if (item.section) sectionCounts[item.section] = (sectionCounts[item.section] || 0) + 1;
      });
      if (fieldNames.length === 0) return;
      // Label the control by the dominant section; fall back to the table's
      // first cell text when fields have no coverage section (e.g. the insured
      // info / address tables, whose section is "General").
      let label = Object.entries(sectionCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
      if (!label || label.toLowerCase() === 'general') {
        const firstCell = (table.querySelector('td, th')?.textContent || '').trim().replace(/\s+/g, ' ');
        if (firstCell) label = firstCell.length > 40 ? `${firstCell.slice(0, 40)}…` : firstCell;
      }
      if (!label) label = 'this section';

      // Capture the heading element(s) sitting directly above the table (the
      // section's title) BEFORE the wrap is inserted, so removing the section
      // also dims its header — matching what export strips.
      const headerEls: HTMLElement[] = [];
      let prev = table.previousElementSibling as HTMLElement | null;
      for (let scanned = 0; prev && scanned < 3 && headerEls.length < 2; scanned++) {
        const text = (prev.textContent || '').trim();
        if (!text) { prev = prev.previousElementSibling as HTMLElement | null; continue; } // skip empties
        const isHeadingTag = /^H[1-6]$/.test(prev.tagName);
        // Mirrors export's heading stripper: a <p> qualifies when it's short
        // plain text, or — if it contains editable fields (e.g. a bold
        // "{{insured_name}}" line above each coverage table) — when bold.
        const hasField = !!prev.querySelector('.rn-field');
        const isBold = !!prev.querySelector('strong, b');
        const isHeadingPara = prev.tagName === 'P' && (hasField ? isBold : (text.length <= 60 || isBold));
        if (!isHeadingTag && !isHeadingPara) break;
        headerEls.push(prev);
        prev = prev.previousElementSibling as HTMLElement | null;
      }

      const wrap = document.createElement('div');
      wrap.className = 'rn-section-wrap';
      table.parentNode?.insertBefore(wrap, table);
      const toolbar = document.createElement('div');
      toolbar.className = 'rn-section-toolbar';
      const button = document.createElement('button');
      button.type = 'button';
      toolbar.appendChild(button);
      wrap.appendChild(toolbar);
      wrap.appendChild(table);

      const meta: SectionMeta = { wrap, button, fieldNames, items, label, headerEls };
      button.addEventListener('click', (ev) => {
        ev.preventDefault();
        const currentlyRemoved = wrap.classList.contains('rn-section-removed');
        if (currentlyRemoved) {
          setRestored(prev => { const next = new Set(prev); meta.fieldNames.forEach(n => next.add(n)); return next; });
          setRemovedLocal(prev => { const next = new Set(prev); meta.fieldNames.forEach(n => next.delete(n)); return next; });
          restoreRef.current(meta.items);
        } else {
          setRemovedLocal(prev => { const next = new Set(prev); meta.fieldNames.forEach(n => next.add(n)); return next; });
          setRestored(prev => { const next = new Set(prev); meta.fieldNames.forEach(n => next.delete(n)); return next; });
          removeRef.current(meta.fieldNames);
        }
      });
      sections.push(meta);
    });
    sectionsRef.current = sections;

    // Per-ROW remove controls for finer ("individual area") control: when a
    // table has multiple field-bearing rows, each row gets a hover ✕ that
    // removes just that row's fields (export then strips the row).
    const rows: SectionMeta[] = [];
    container.querySelectorAll<HTMLElement>('table').forEach(table => {
      const fieldRows = Array.from(table.querySelectorAll<HTMLElement>('tr')).filter(tr => tr.querySelector('.rn-field'));
      if (fieldRows.length < 2) return; // single-row tables: the section button is enough
      fieldRows.forEach(tr => {
        const rowFieldNames: string[] = [];
        const rowItems: RestoreItem[] = [];
        let rowLabel = '';
        tr.querySelectorAll<HTMLElement>('.rn-field').forEach(el => {
          const n = el.getAttribute('data-field');
          if (!n) return;
          rowFieldNames.push(n);
          rowItems.push(restoreItemFromSpan(el, n));
        });
        if (rowFieldNames.length === 0) return;
        // Label the row by its first cell text (e.g. a coverage/limit name).
        rowLabel = (tr.querySelector('td, th')?.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 60) || 'row';

        const lastCell = tr.querySelector<HTMLElement>('td:last-child, th:last-child');
        if (!lastCell) return;
        if (!lastCell.style.position) lastCell.style.position = 'relative';
        const rbtn = document.createElement('button');
        rbtn.type = 'button';
        rbtn.className = 'rn-row-remove';
        rbtn.title = `Remove "${rowLabel}"`;
        rbtn.textContent = '✕';
        lastCell.appendChild(rbtn);

        const rowMeta: SectionMeta = { wrap: tr, button: rbtn, fieldNames: rowFieldNames, items: rowItems, label: rowLabel };
        rbtn.addEventListener('click', (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const allRemoved = rowFieldNames.every(n => isRemovedRef.current(n));
          if (allRemoved) {
            setRestored(prev => { const next = new Set(prev); rowFieldNames.forEach(n => next.add(n)); return next; });
            setRemovedLocal(prev => { const next = new Set(prev); rowFieldNames.forEach(n => next.delete(n)); return next; });
            restoreRef.current(rowItems);
          } else {
            setRemovedLocal(prev => { const next = new Set(prev); rowFieldNames.forEach(n => next.add(n)); return next; });
            setRestored(prev => { const next = new Set(prev); rowFieldNames.forEach(n => next.delete(n)); return next; });
            removeRef.current(rowFieldNames);
          }
        });
        rows.push(rowMeta);
      });
    });
    rowsRef.current = rows;

    return () => container.removeEventListener('input', handleInput);
  }, [html]);

  // Sync visual state (values, removed styling, controls) whenever data changes.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || html == null) return;
    const valueByName = new Map<string, string>();
    fields.forEach(f => { valueByName.set(f.fieldName, f.fieldValue || ''); });

    container.querySelectorAll<HTMLElement>('.rn-field').forEach(el => {
      const name = el.getAttribute('data-field');
      if (!name) return;
      const removed = isRemoved(name);
      el.classList.toggle('rn-removed', removed);
      el.setAttribute('contenteditable', removed ? 'false' : 'true');
      el.setAttribute('spellcheck', 'false');
      const desired = valueByName.get(name) ?? '';
      if (document.activeElement !== el && el.textContent !== desired) {
        el.textContent = desired;
      }
    });

    // Update each section's removed state + button label.
    sectionsRef.current.forEach(meta => {
      const allRemoved = meta.fieldNames.length > 0 && meta.fieldNames.every(isRemoved);
      meta.wrap.classList.toggle('rn-section-removed', allRemoved);
      meta.button.textContent = allRemoved ? `Restore ${meta.label}` : `Remove ${meta.label}`;
      meta.button.className = `rn-section-btn ${allRemoved ? 'rn-restore' : 'rn-remove'}`;
      // Dim the section's heading too — export removes it with the section.
      (meta.headerEls || []).forEach(h => h.classList.toggle('rn-heading-removed', allRemoved));
    });

    // Update each row's removed state (dim the row + flip the ✕/↩ control).
    rowsRef.current.forEach(meta => {
      const allRemoved = meta.fieldNames.length > 0 && meta.fieldNames.every(isRemoved);
      meta.wrap.classList.toggle('rn-row-removed', allRemoved);
      meta.button.textContent = allRemoved ? '↩' : '✕';
      meta.button.title = allRemoved ? `Restore "${meta.label}"` : `Remove "${meta.label}"`;
    });
  }, [fields, html, isRemoved]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">{error}</div>;
  }

  return (
    <>
      <style>{DOCUMENT_VIEW_CSS}</style>
      <div className="bg-white shadow-sm rounded-lg mx-auto" style={{ maxWidth: '850px' }}>
        <div
          ref={containerRef}
          className="rn-doc px-12 py-10"
          dangerouslySetInnerHTML={{ __html: html || '' }}
        />
      </div>
    </>
  );
};

const ProposalEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [editedFields, setEditedFields] = useState<ProposalField[]>([]);
  // Latest editedFields for use inside stable callbacks without re-binding them.
  const editedFieldsRef = useRef(editedFields);
  editedFieldsRef.current = editedFields;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [removedIndices, setRemovedIndices] = useState<Set<number>>(new Set());
  const [viewMode, setViewMode] = useState<'document' | 'fields'>('document');
  // null = unknown (still checking), true/false once the editable-html endpoint responds
  const [docSupported, setDocSupported] = useState<boolean | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

  const fetchProposal = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/proposals/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to load proposal');
      }

      const data = await res.json();
      setProposal(data.proposal);
      setEditedFields(JSON.parse(JSON.stringify(data.proposal.fields || [])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load proposal');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, id, token]);

  useEffect(() => {
    if (id && token) {
      fetchProposal();
    }
  }, [id, token, fetchProposal]);

  const handleFieldChange = (index: number, newValue: string) => {
    const updated = [...editedFields];
    updated[index] = { ...updated[index], fieldValue: newValue };
    setEditedFields(updated);
    setHasChanges(true);
    setSaveStatus(null);
  };

  // Update every field sharing a placeholder name. Used by the document view,
  // which binds editable boxes by fieldName (the same token can appear more
  // than once in a template, and they all carry the same value).
  const handleFieldChangeByName = useCallback((fieldName: string, newValue: string) => {
    setEditedFields(prev => prev.map(f => (f.fieldName === fieldName ? { ...f, fieldValue: newValue } : f)));
    setHasChanges(true);
    setSaveStatus(null);
  }, []);

  // Document view section removal: mark every field in a section removed.
  const handleRemoveFieldsByName = useCallback((names: string[]) => {
    const set = new Set(names);
    setRemovedIndices(prev => {
      const next = new Set(prev);
      editedFieldsRef.current.forEach((f, i) => { if (set.has(f.fieldName)) next.add(i); });
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  }, []);

  // Restore a removed section. Fields still present just get un-removed; fields
  // that were dropped on a prior save are re-created (empty) from their context.
  const handleRestoreFields = useCallback((items: RestoreItem[]) => {
    const restoreNames = new Set(items.map(it => it.fieldName));
    const cur = editedFieldsRef.current;
    setRemovedIndices(prev => {
      const next = new Set(prev);
      cur.forEach((f, i) => { if (restoreNames.has(f.fieldName)) next.delete(i); });
      return next;
    });
    const present = new Set(cur.map(f => f.fieldName));
    const toAdd = items.filter(it => !present.has(it.fieldName));
    if (toAdd.length > 0) {
      setEditedFields(prev => [
        ...prev,
        ...toAdd.map(it => ({
          fieldName: it.fieldName,
          fieldValue: '',
          sectionName: it.section || 'General',
          confidence: 'low' as const,
          ...(it.label ? { displayLabel: it.label } : {}),
          // Multi-template: export maps a field to its template via these.
          ...(it.templateId ? { templateId: it.templateId } : {}),
          ...(it.placeholder ? { placeholder: it.placeholder } : {}),
        })),
      ]);
    }
    setHasChanges(true);
    setSaveStatus(null);
  }, []);

  const handleRemoveField = (index: number) => {
    setRemovedIndices(prev => new Set(prev).add(index));
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleRestoreField = (index: number) => {
    setRemovedIndices(prev => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  };

  // Remove/restore a set of field indices at once (used for repeating sub-rows,
  // e.g. an unused "Location 3" block within a section).
  const handleRemoveIndices = (indices: number[]) => {
    setRemovedIndices(prev => {
      const next = new Set(prev);
      indices.forEach(i => next.add(i));
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleRestoreIndices = (indices: number[]) => {
    setRemovedIndices(prev => {
      const next = new Set(prev);
      indices.forEach(i => next.delete(i));
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleRemoveSection = (sectionName: string) => {
    const indices = editedFields
      .map((f, i) => (f.sectionName || 'General') === sectionName ? i : -1)
      .filter(i => i !== -1);
    setRemovedIndices(prev => {
      const next = new Set(prev);
      indices.forEach(i => next.add(i));
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleRestoreSection = (sectionName: string) => {
    const indices = editedFields
      .map((f, i) => (f.sectionName || 'General') === sectionName ? i : -1)
      .filter(i => i !== -1);
    setRemovedIndices(prev => {
      const next = new Set(prev);
      indices.forEach(i => next.delete(i));
      return next;
    });
    setHasChanges(true);
    setSaveStatus(null);
  };

  const handleSupportResolved = useCallback((supported: boolean) => {
    setDocSupported(supported);
    if (!supported) setViewMode('fields');
  }, []);

  // Get active fields (excluding removed ones) for saving/exporting
  const activeFields = editedFields.filter((_, i) => !removedIndices.has(i));

  // Field names currently removed (for the document view's section controls).
  const removedFieldNames = useMemo(
    () => new Set(editedFields.filter((_, i) => removedIndices.has(i)).map(f => f.fieldName)),
    [editedFields, removedIndices]
  );

  const handleSave = async () => {
    if (!proposal) return;
    setSaving(true);
    setSaveStatus(null);

    try {
      const res = await fetch(`${apiUrl}/api/proposals/${proposal.id}/fields`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fields: activeFields }),
      });

      if (res.ok) {
        setSaveStatus('Saved successfully');
        setHasChanges(false);
        // Reset state to reflect saved fields (removed ones are gone)
        setEditedFields(activeFields);
        setRemovedIndices(new Set());
        setProposal({ ...proposal, fields: activeFields });
      } else {
        setSaveStatus('Failed to save');
      }
    } catch (err) {
      setSaveStatus('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!proposal) return;
    if (!window.confirm('Mark this proposal as final? You can still edit it later.')) return;

    try {
      const res = await fetch(`${apiUrl}/api/proposals/${proposal.id}/fields`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ fields: activeFields, status: 'final' }),
      });

      if (res.ok) {
        setEditedFields(activeFields);
        setRemovedIndices(new Set());
        setProposal({ ...proposal, status: 'final', fields: activeFields });
        setHasChanges(false);
        setSaveStatus('Proposal finalized');
      }
    } catch (err) {
      setSaveStatus('Failed to finalize');
    }
  };

  const handleExport = async (format: 'pdf' | 'docx' | 'xlsx') => {
    if (!proposal) return;
    setExporting(true);

    try {
      // Save first if there are unsaved changes
      if (hasChanges) {
        await handleSave();
      }

      const res = await fetch(`${apiUrl}/api/proposals/${proposal.id}/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ format }),
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);

        if (format === 'pdf') {
          // PDF opens in a new tab (LibreOffice converts xlsx/docx → real PDF;
          // legacy docx fallback may still be styled HTML)
          window.open(url, '_blank');
        } else {
          const ext = format === 'xlsx' ? 'xlsx' : 'docx';
          const a = document.createElement('a');
          a.href = url;
          a.download = `${proposal.title}.${ext}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } else {
        setError('Failed to export proposal');
      }
    } catch (err) {
      setError('Failed to export proposal');
    } finally {
      setExporting(false);
    }
  };

  // Format placeholder names as readable labels
  const formatLabel = (fieldName: string): string => {
    // XLSX-encoded field: xlsx::SheetName::A5 → just show the cell address;
    // the section header already shows the sheet name.
    if (fieldName.startsWith('xlsx::')) {
      const rest = fieldName.substring('xlsx::'.length);
      const lastSep = rest.lastIndexOf('::');
      if (lastSep >= 0) {
        return `Cell ${rest.substring(lastSep + 2)}`;
      }
    }
    // Remove known prefixes for cleaner display
    const prefixes = ['gl_', 'prop_', 'wc_', 'auto_', 'umb_', 'client_', 'agent_', 'agency_', 'total_', 'policy_'];
    let label = fieldName;
    for (const prefix of prefixes) {
      if (label.startsWith(prefix)) {
        label = label.substring(prefix.length);
        break;
      }
    }
    return label
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const isXlsxTemplate = proposal?.template?.originalFileType === 'xlsx';

  // For multi-template proposals, prefix each section with its source template
  // so identically-named sections from different templates stay separate.
  const isMultiTemplate = (proposal?.templateIds?.length || 0) > 1;
  const templateOrder = proposal?.templateIds || [];

  // Group fields by section (template-qualified when multi-template)
  const groupedFields = editedFields.reduce<Record<string, Array<{ field: ProposalField; index: number }>>>(
    (acc, field, index) => {
      let section = field.sectionName || 'General';
      if (isMultiTemplate && field.templateId) {
        const n = templateOrder.indexOf(field.templateId);
        if (n >= 0) section = `Template ${n + 1} — ${section}`;
      }
      if (!acc[section]) acc[section] = [];
      acc[section].push({ field, index });
      return acc;
    },
    {}
  );

  // Repeating-unit key for a field within its section (e.g. "Location 1").
  // Prefers the persisted rowLabel; falls back to the displayLabel minus its
  // prior/renewal column qualifier so existing proposals group too.
  const getRowKey = (field: ProposalField): string | null => {
    if (field.rowLabel) return field.rowLabel;
    if (field.displayLabel) {
      const stripped = field.displayLabel.replace(/\s*\((prior|renewal|current|expiring|proposed|col\d+)\)\s*$/i, '').trim();
      return stripped || null;
    }
    return null;
  };

  const getConfidenceBadge = (confidence?: string) => {
    switch (confidence) {
      case 'high':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">High</span>;
      case 'medium':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">Medium</span>;
      case 'low':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Low</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">{error || 'Proposal not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{proposal.title}</h1>
                <div className="flex items-center gap-3 mt-1">
                  {proposal.template && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Template: {proposal.template.title}
                    </span>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                    proposal.status === 'final'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }`}>
                    {proposal.status === 'final' ? 'Final' : 'Draft'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {saveStatus && (
                <span className={`text-sm ${saveStatus.includes('Failed') ? 'text-red-500' : 'text-green-500'}`}>
                  {saveStatus}
                </span>
              )}

              {/* View toggle — only when the document view is available (DOCX templates).
                  Multi-template proposals render every template in order. */}
              {docSupported !== false && (
                <div className="flex items-center rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <button
                    onClick={() => setViewMode('document')}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      viewMode === 'document'
                        ? 'bg-primary text-white'
                        : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Document
                  </button>
                  <button
                    onClick={() => setViewMode('fields')}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      viewMode === 'fields'
                        ? 'bg-primary text-white'
                        : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    Fields
                  </button>
                </div>
              )}

              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  saving || !hasChanges
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-default'
                    : 'bg-primary text-white hover:bg-blue-600'
                }`}
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15,9H5V5H15M12,19A3,3 0 0,1 9,16A3,3 0 0,1 12,13A3,3 0 0,1 15,16A3,3 0 0,1 12,19M17,3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V7L17,3Z" />
                  </svg>
                )}
                Save
              </button>

              {proposal.status !== 'final' && (
                <button
                  onClick={handleFinalize}
                  className="px-4 py-2 border border-green-500 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                >
                  Finalize
                </button>
              )}

              <div className="relative">
                <button
                  disabled={exporting}
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  {exporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent" />
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" />
                    </svg>
                  )}
                  Export
                </button>
                {showExportMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                    <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                      <button
                        onClick={() => { setShowExportMenu(false); handleExport('pdf'); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                      >
                        Print / Save as PDF
                      </button>
                      {isXlsxTemplate ? (
                        <button
                          onClick={() => { setShowExportMenu(false); handleExport('xlsx'); }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                        >
                          Export as XLSX
                        </button>
                      ) : (
                        <button
                          onClick={() => { setShowExportMenu(false); handleExport('docx'); }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                        >
                          Export as DOCX
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Document view: rendered template(s) with inline editable boxes (DOCX templates) */}
        {docSupported !== false && (
          <div className={viewMode === 'document' ? '' : 'hidden'}>
            <ProposalDocumentView
              apiUrl={apiUrl}
              proposalId={proposal.id}
              token={token}
              fields={editedFields}
              removedFieldNames={removedFieldNames}
              onFieldChangeByName={handleFieldChangeByName}
              onRemoveFields={handleRemoveFieldsByName}
              onRestoreFields={handleRestoreFields}
              onSupportResolved={handleSupportResolved}
            />
          </div>
        )}

        {/* Fields view: flat list of editable field values */}
        <div className={(viewMode === 'fields' || docSupported === false) ? '' : 'hidden'}>
        {Object.entries(groupedFields).length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No fields were generated. The AI may not have detected any fields in the template.
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedFields).map(([sectionName, fields]) => {
              const allRemoved = fields.every(({ index }) => removedIndices.has(index));
              const someRemoved = fields.some(({ index }) => removedIndices.has(index));

              return (
              <div key={sectionName} className={`group/section bg-white dark:bg-gray-800 rounded-lg border overflow-hidden transition-opacity ${
                allRemoved ? 'border-red-200 dark:border-red-800 opacity-50' : 'border-gray-200 dark:border-gray-700'
              }`}>
                <div className={`px-6 py-3 border-b flex items-center justify-between ${
                  allRemoved
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                }`}>
                  <h2 className={`text-lg font-semibold ${allRemoved ? 'text-red-400 line-through' : 'text-gray-900 dark:text-white'}`}>{sectionName}</h2>
                  {allRemoved ? (
                    <button
                      onClick={() => handleRestoreSection(sectionName)}
                      className="px-3 py-1 text-xs font-medium text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    >
                      Restore Section
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRemoveSection(sectionName)}
                      className="px-3 py-1 text-xs font-medium text-red-500 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover/section:opacity-100 hover:!opacity-100"
                      style={{ opacity: someRemoved ? 1 : undefined }}
                    >
                      Remove Section
                    </button>
                  )}
                </div>
                {!allRemoved && (() => {
                  const renderField = ({ field, index }: { field: ProposalField; index: number }) => {
                    const isRemoved = removedIndices.has(index);
                    return (
                    <div key={index} className={`group/field relative ${isRemoved ? 'opacity-40' : ''}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className={`text-sm font-medium ${isRemoved ? 'text-red-400 line-through' : 'text-gray-700 dark:text-gray-300'}`} title={`{{${field.fieldName}}}`}>
                          {field.displayLabel || formatLabel(field.fieldName)}
                        </label>
                        <div className="flex items-center gap-2">
                          {getConfidenceBadge(field.confidence)}
                          {isRemoved ? (
                            <button
                              onClick={() => handleRestoreField(index)}
                              className="px-2 py-0.5 text-xs text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                            >
                              Restore
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRemoveField(index)}
                              className="px-2 py-0.5 text-xs text-red-400 border border-transparent rounded hover:border-red-200 dark:hover:border-red-800 hover:text-red-500 transition-colors opacity-0 group-hover/field:opacity-100"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                      {!isRemoved && (
                      <textarea
                        value={field.fieldValue}
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        rows={field.fieldValue.length > 200 ? 4 : field.fieldValue.length > 100 ? 3 : 2}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-white text-sm resize-y transition-shadow"
                        placeholder="Enter value..."
                      />
                      )}
                    </div>
                    );
                  };

                  // Group a section's fields into repeating sub-rows (e.g.
                  // "Location 1".."Location 5") so each can be removed as a unit.
                  const rowMap = new Map<string, Array<{ field: ProposalField; index: number }>>();
                  const ungrouped: Array<{ field: ProposalField; index: number }> = [];
                  fields.forEach((fi) => {
                    const key = getRowKey(fi.field);
                    if (key) {
                      const arr = rowMap.get(key) || [];
                      arr.push(fi);
                      rowMap.set(key, arr);
                    } else {
                      ungrouped.push(fi);
                    }
                  });
                  const useRows = rowMap.size >= 2;

                  if (!useRows) {
                    return <div className="p-6 space-y-5">{fields.map(renderField)}</div>;
                  }

                  return (
                    <div className="p-6 space-y-4">
                      {ungrouped.length > 0 && (
                        <div className="space-y-5">{ungrouped.map(renderField)}</div>
                      )}
                      {Array.from(rowMap.entries()).map(([rowKey, rowFields]) => {
                        const rowIndices = rowFields.map((f) => f.index);
                        const rowAllRemoved = rowIndices.every((i) => removedIndices.has(i));
                        return (
                          <div key={rowKey} className={`group/row rounded-lg border transition-opacity ${
                            rowAllRemoved ? 'border-red-200 dark:border-red-800 opacity-50' : 'border-gray-200 dark:border-gray-700'
                          }`}>
                            <div className={`px-4 py-2 flex items-center justify-between border-b ${
                              rowAllRemoved ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                            }`}>
                              <span className={`text-sm font-medium ${rowAllRemoved ? 'text-red-400 line-through' : 'text-gray-700 dark:text-gray-200'}`}>{rowKey}</span>
                              {rowAllRemoved ? (
                                <button
                                  onClick={() => handleRestoreIndices(rowIndices)}
                                  className="px-2 py-0.5 text-xs text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                                >
                                  Restore
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleRemoveIndices(rowIndices)}
                                  className="px-2 py-0.5 text-xs text-red-500 dark:text-red-400 border border-red-200 dark:border-red-800 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover/row:opacity-100"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            {!rowAllRemoved && (
                              <div className="p-4 space-y-4">{rowFields.map(renderField)}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ProposalEditor;
